/**
 * API Wrapper
 *
 * This file instrument the access to the various APIs required to run the player
 */

import {Promise} from 'ereader-core/dist/utils.Promise.js';
import { generateTitle } from '../helpers/title.js';
//Constants
export let REVIEW_OTHER_ID = 7;
export const LIMIT_EXCEEDED = 'limit exceeded';
export const NO_ACCESS = 'no access';
export const BAD_TOC = 'bad toc';
export const ACCOUNT_LIMIT_EXCEEDED = 'REACHED_BOOK_LIMIT';
export const ACCOUNT_OK = 'ACCOUNT_OK';
export const ACCOUNT_FLAGGED = 'ACCOUNT_FLAGGED';
export const ACCOUNT_SUSPICIOUS = 'ACCOUNT_SUSPICIOUS';

let dummyLogger = {
    serverLog: function(){},
    serverError: function(){}
}

export const  chapter_request = {};

let uids = {};

// function only for use in tests
export function setUID(appid, userid){
    uids[appid] = userid;
}

function graphQlResponse(query = '', logger=dummyLogger) {
    // Commentng this line and saving this in codebase so that it can be used for future reference for development.
    // const url = "https://pranay.live.dev.cheggnet.com/study/_ajax/persistquerygraphql";
    const url = "/study/_ajax/persistquerygraphql";
    const Query = {query: query};
    if (typeof C !== "undefined" && C.global && C.global.csrfToken) {
        Query.token = C.global.csrfToken;
    }
    const body = JSON.stringify(Query);
    return fetch(url, {
        method: "POST",
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
        body: body, 
    }).then(response => response.json())
    .then(data => {
      return Promise.resolve(data.data);
    })
    .catch((error) => {
        logger.serverLog(`Error fetching data in Solution Player for query: ${Query}`, error);
        return;
    });
}

/**
 * retirn TOC for Solution player. 
 * 
 * @param {*} isbn13 
 * @param {*} partner 
 * @param {*} user_id 
 * @param {*} app_id 
 * @param {*} logger 
 */
export function initModel(isbn13, partner, user_id, app_id, logger=dummyLogger) {
    const query = {
        "operationName": "getTOC",
        "variables": {
            "isbn13": isbn13
        }
    };

    return graphQlResponse(query, logger).then(data => {
        if (!data) {
            throw {status : 500, type:BAD_TOC};
        }

        const is_subscriber = true;
        const review_data = !isKeyDefined(data, 'textbook_solution.reviewReason') ? [] : data.textbook_solution.reviewReason;
        const chapters = !isKeyDefined(data, 'textbook_solution.toc.chapters') ? [] : data.textbook_solution.toc.chapters;
        const bookmark_data = !isKeyDefined(data, 'textbook_solution.bookmarkData') ? [] : data.textbook_solution.bookmarkData;
        const has_tbs = false;

        if (!chapters || chapters.length == 0) {
            throw {status : 500, type:BAD_TOC};
        }

        return {
            has_tbs,
            review_data,
            is_subscriber,
            chapters,
            bookmark_data
        };
    });
}

/**
 * Get a solution data, including steps and comments.
 *
 * @param string id problem id
 * @param string [identifier] - used for providing unique ids for steps. If not provided id will be used
 *
 * @returns promise
 */
export function getSolutionById(isbn, id, identifier, partner, user_id, do_account_sharing, logger=dummyLogger, is_moderator=false, chapterId) {
    const query = {
            "operationName": "getSolutionDetails",
            "variables": {
                "isbn13": isbn,
                "chapterId": chapterId,
                "problemId": id
            }
    };

    return graphQlResponse(query, logger).then(data => {
        if (!data) {
            throw `Unable to fetch data, API request failed for isbn ${isbn}`;
        }
        const bookmark_count = !isKeyDefined(data, 'textbook_solution.chapter.0.problems.0.userAssetCount') ? 0 : data.textbook_solution.chapter[0].problems[0].userAssetCount;
        const solution_id = !isKeyDefined(data, 'textbook_solution.chapter.0.problems.0.solutions.0.solutionId') ? '' : data.textbook_solution.chapter[0].problems[0].solutions[0].solutionId;
        const total_steps = !isKeyDefined(data, 'textbook_solution.chapter.0.problems.0.solutions.0.totalStep') ? 0 : data.textbook_solution.chapter[0].problems[0].solutions[0].totalStep;
        let step_comments = !isKeyDefined(data, 'textbook_solution.chapter.0.problems.0.solutions.0.steps') ? [] : data.textbook_solution.chapter[0].problems[0].solutions[0].steps;
        const review = !isKeyDefined(data, 'textbook_solution.chapter.0.problems.0.solutions.0.reviews') ? '' : data.textbook_solution.chapter[0].problems[0].solutions[0].reviews;
        const user_reviews = !isKeyDefined(data, 'textbook_solution.chapter.0.problems.0.solutions.0.userReviews') ? [] : data.textbook_solution.chapter[0].problems[0].solutions[0].userReviews;
        const access_restriction = !isKeyDefined(data, 'textbook_solution.chapter.0.problems.0.solutionV2.0.accessDetails.accessRestrictions.0') ? '' : data.textbook_solution.chapter[0].problems[0].solutionV2[0].accessDetails.accessRestrictions[0];
        const has_access = !isKeyDefined(data, 'textbook_solution.chapter.0.problems.0.solutionV2.0.accessDetails.hasAccess') ? false : data.textbook_solution.chapter[0].problems[0].solutionV2[0].accessDetails.hasAccess;
        const solutionObject = !isKeyDefined(data, 'textbook_solution.chapter.0.problems.0.solutions') ? false : data.textbook_solution.chapter[0].problems[0].solutions;
        const steps = !isKeyDefined(data, 'textbook_solution.chapter.0.problems.0.solutionV2.0.steps') ? false : data.textbook_solution.chapter[0].problems[0].solutionV2[0].steps;
        const coverName = !isKeyDefined(data, 'textbook_solution.coverName') ? '' : data.textbook_solution.coverName;
        const editionNumber = !isKeyDefined(data, 'textbook_solution.editionNumber') ? 0 : data.textbook_solution.editionNumber;
        const chapterName = !isKeyDefined(data, 'textbook_solution.chapter.0.chapterName') ? '0' : data.textbook_solution.chapter[0].chapterName;
        const problemName = !isKeyDefined(data, 'textbook_solution.chapter.0.problems.0.problemName') ? '' : data.textbook_solution.chapter[0].problems[0].problemName;
        const problemHtml =  !isKeyDefined(data, 'textbook_solution.chapter.0.problems.0.problemHtml') ? '' : data.textbook_solution.chapter[0].problems[0].problemHtml;

        //making the page title based on the problem, solution and isbn details. https://chegg.atlassian.net/browse/STUDY-17274
        let pageTitle = '';
        try{
            pageTitle = generateTitle(coverName, editionNumber, chapterName, problemName, problemHtml, total_steps);
        }catch(err){
            console.error('Error: While generating the page title: ',err);
            pageTitle = document.title;
        }

        const counts = {};
        counts.like = (review && review.positiveReviewCount) ? review.positiveReviewCount : 0;
        counts.dislike = (review && review.negativeReviewCount) ? review.negativeReviewCount : 0 ;
        const comments = [];
        
        if (step_comments) {
            step_comments.forEach((step)=> {
                if (step.commentList) {
                    step.commentList.forEach((comment) => {
                        comments.push(comment);
                    });
                }
            });
        }

        //Handle account sharing restriction
        if(solutionObject.length && do_account_sharing && access_restriction == ACCOUNT_FLAGGED) {
            let error = handleAccountSharing(solutionObject[0], logger);
            throw error;
        }

        //Handle no access
        if(solutionObject.length && !has_access) {
          let error = parseError(solutionObject[0], logger, access_restriction);
          throw error;
        }
        const solutionResult = {steps, comments, solution_id, total_steps, access_restriction, pageTitle, problemHtml};
        const bookmarkResult = {bookmark_count: bookmark_count};
        const reviewResult = {counts, user_reviews};

        return {solutionResult, bookmarkResult, reviewResult};
    });
}

function isKeyDefined(obj, key) {
    return key.split(".").reduce(function(o, x) {
        return (typeof o == "undefined" || o === null) ? o : o[x];
    }, obj);
}

/**
 * Get html description for the problem
 * 
 * @param {*} problem 
 * @param {*} logger 
 */
export function getProblemHTML(problem, logger){
    return problem.problemHtml || "";
}

/**
 * Post a Comment for Solution based on stepIndex and solution ID
 * 
 * @param {*} comment 
 * @param {*} partner 
 * @param {*} user_id 
 * @param {*} logger 
 */
export function postComment(comment, partner, user_id, logger=dummyLogger){
    const query = {
        "operationName": "postTbsComment",
        "variables": {
            "solutionId": comment.solution_id,
            "stepIndex": comment.stepIndex,
            "comment": comment.comment,
            "solutionStepCommentType": "1",
            "problemId": comment.problemId
        }
    };

    return graphQlResponse(query, logger).then(data => {
        const commentResponse = data && data.post_tbs_comment;

        if (!commentResponse.result) {
            if (commentResponse.error.indexOf('Profanity Filter') > -1) {
                alert('Sorry, we have detected use of an inappropriate word or phrase. Please edit and try again.');
            }
 
            return Promise.reject(commentResponse.error);
        }
        return comment;
    });
}

/**
 * Remove a comment. Only Moderators can remove comment. 
 * 
 * @param {*} comment_id 
 * @param {*} solution_id 
 * @param {*} partner 
 * @param {*} app_id 
 * @param {*} problem_id 
 */
export function removeComment(comment_id, solution_id, partner, app_id, problem_id) {
    const query = {
        "operationName": "deleteTbsComment",
        "variables": {
            'solutionId': solution_id,
            'description': '',
            'commentId': comment_id,
            'problemId': problem_id
        }
    };

    return graphQlResponse(query).then(data => {
        if (data && !data.delete_tbs_comment) {
            return Promise.reject({});
        }

        return {comment_id: comment_id};
    });
}

/**
 * Post Reviews for the Problem
 * 
 * @param {*} review 
 * @param {*} partner 
 * @param {*} user_id 
 * @param {*} logger 
 */
export function postReview(review, partner, user_id, logger=dummyLogger) {
    const reviewReasons = [
        {
            reviewReasonId : review.reason
        }
    ];
    if (review.comment) {
        reviewReasons[0].reviewComment = review.comment;
    }

    const reviewId = review.id || '';
    
    const query = {
        "operationName": "postReview",
        "variables": {
            'reviewId': reviewId,
            'entityType': 'SOLUTION',
            'entityId': review.solutionId,
            'reviewType': 'LIKE_DISLIKE',
            'reviewValue': review.reviewValue,
            'reviewReason': JSON.stringify(reviewReasons).replace(/[\"]/g, '\\"'),
            'metadata': [
                {
                    'name': 'chapterId',
                    'value': review.chapterId
                },
                {
                    'name': 'isbn13',
                    'value': review.isbn13
                }
            ]
        }
    };

    return graphQlResponse(query, logger).then(data => {
        const reviewId = !isKeyDefined(data, 'post_review.id') ? '' : data.post_review.id;

        if (!reviewId) {
            return Promise.reject(reviewId);
        }

        return {
            ...review,
            id: reviewId
        }
    });
}

/**
 * Post Bookmark for the problem
 * 
 * @param {*} bookmark 
 * @param {*} partner 
 * @param {*} user_id 
 * @param {*} logger 
 */
export function postBookmark(bookmark, partner, user_id, logger=dummyLogger) {
    let bookmarkAction = bookmark.isBookmarked ? 'removeBookmark' : 'saveBookmark';


    const query = {
        "operationName": bookmarkAction,
        "variables": {
            "assetType": "TBS",
            "entityId": bookmark.id,
            "metadata": [
                {
                    "name": "chapter_id",
                    "value": bookmark.chapterId
                },
                {
                    "name": "isbn13",
                    "value": bookmark.isbn13
                }
            ]
        }
    };

    return graphQlResponse(query, logger).then(data => {
        if (bookmark.isBookmarked && !data.remove_bookmark) {
            return Promise.reject({});
        } else if (!bookmark.isBookmarked && !data.save_bookmark) {
            return Promise.reject({});
        }


        return {
            ...bookmark
        };
    });
}

function handleAccountSharing(response, logger){
    logger && logger.serverLog('account sharing', response);
  
    return {
        status : 403,
        reason : ACCOUNT_FLAGGED
    };
  }

function parseError(response, logger, access_restriction= '') {
    logger && logger.serverLog('ERROR: parseError', response);

    if (access_restriction == ACCOUNT_LIMIT_EXCEEDED) {
        return {
            status : 403,
            reason : LIMIT_EXCEEDED
        };
    }

    return {
        status : 403,
        reason : NO_ACCESS
    };
}



// WEBPACK FOOTER //
// ./src/modules/apiWrapper.js